import { useState, Fragment } from "react";
import { IconButton, Menu, MenuItem, Tooltip, ListItemIcon, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { AlternateEmail, CopyrightOutlined, Login, Logout, PaletteOutlined, Settings as SettingsIcon } from '@mui/icons-material';
import { useKeycloak } from '../Keycloak';
import { isIframe, sendDataToIframe, getManagerSearchParams, ConditionalWrapper, isMobileApp, sendDataToMobileApp } from "../Utilities";
import Copyright from "./Copyright";

export default function Settings({ invertColors, toggleInvertColors, handleOpenSupport, onShowWhatsNew }) {

    const [anchorEl, setAnchorEl] = useState(null)
    const [openCopyright, setOpenCopyright] = useState(false)

    const { t, i18n } = useTranslation()
    const { doLogout, isLoggedIn, doLogin, isUsersPortal } = useKeycloak()
    const { isSingleProject, isManager } = getManagerSearchParams()

    const curLang = i18n.language
    const isOpen = Boolean(anchorEl)
    const popoverId = isOpen ? 'basic-menu' : undefined
    const disableCredentialControl = 
        (isManager && !isSingleProject) || // if accessed via manager and it is not a single project OR
        (isIframe && !isManager) // if it is an iframe and not accessed via manager (if customer uses iframe to show single portal)

    const handleOpen = (event) => {
        if (anchorEl !== event?.currentTarget) {
            setAnchorEl(event.currentTarget)
        }
    }

    const handleOpenCopyright = () => {
        setOpenCopyright(true)
    }
    
    const handleCloseSettings = () => {
        if (anchorEl) {
            setAnchorEl(null)
        }
    }

    const handleChangeLanguage = (ln) => {
        handleCloseSettings()
        i18n.changeLanguage(ln)
        window.localStorage.setItem('btv_lang', ln)
    }

    const handleToggleInvertColors = () => {
        handleCloseSettings()
        toggleInvertColors()
    }

    const handleCloseCopyright = () => {
        handleCloseSettings()
        setOpenCopyright(false)
    }

    const handleLogout = () => {
        handleCloseSettings()

        if (isManager && isSingleProject) {
            sendDataToIframe({ type: "goHomeAndLogout" })
        } else {
            doLogout()
        }
        
        isMobileApp && sendDataToMobileApp({ type: "logout" })
    }

    const handleLogin = () => {
        doLogin()
    }

    return (
        <Fragment>
            <Tooltip 
                title={t("user.settings")}
                arrow
                placement="bottom-start"
            >
                <IconButton
                    aria-describedby={popoverId}
                    sx={{ 
                        p: 0,
                        mb: { xs: 0, md: "1px" },
                        ml: { xs: "14px", md: '8px' },
                        color: invertColors ? 'white' : 'primary.main'
                    }}
                    onClick={handleOpen}
                >
                    <SettingsIcon 
                        sx={{ 
                            fontSize: "1.2rem", 
                            pb: "1px"
                        }}
                    />
                </IconButton>
            </Tooltip>

            <Menu
                id={popoverId}
                anchorEl={anchorEl}
                open={isOpen}
                onClose={handleCloseSettings}
                MenuListProps={{ 'aria-labelledby': 'basic-button' }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center'
                }}
                sx={{ pt: 0 }}
            >

                {/* languages */}

                <MenuItem 
                    onClick={() => handleChangeLanguage('de')} 
                    sx={{ backgroundColor: curLang === 'de' ? '#e0e6ec' : 'white' }}
                    selected={curLang === 'de'}
                >
                    <ListItemIcon>
                        <div className="flag flag_de"></div>
                    </ListItemIcon>
                    {t(`user.language.german`)}
                </MenuItem>

                <MenuItem 
                    onClick={() => handleChangeLanguage('en')} 
                    sx={{ backgroundColor: curLang === 'en' ? '#e0e6ec' : 'white' }}
                    selected={curLang === 'en'}
                >
                    <ListItemIcon>
                        <div className="flag flag_en"></div>
                    </ListItemIcon>
                    {t(`user.language.english`)}
                </MenuItem>

                <MenuItem 
                    onClick={() => handleChangeLanguage('nl')} 
                    sx={{ 
                        backgroundColor: curLang === 'nl' ? '#e0e6ec' : 'white', 
                    }}
                    selected={curLang === 'nl'}
                    divider
                >
                    <ListItemIcon>
                        <div className="flag flag_nl"></div>
                    </ListItemIcon>
                    {t(`user.language.dutch`)}
                </MenuItem>

                {/* color */}

                <MenuItem 
                    onClick={handleToggleInvertColors}
                    sx={{ color: invertColors && 'primary.main' }}
                >
                    <ListItemIcon>
                        <PaletteOutlined fontSize="small" sx={{ color: invertColors && 'primary.main' }} />
                    </ListItemIcon>
                    {t("user.color")}
                </MenuItem>

                {/* copyright */}

                <MenuItem onClick={handleOpenCopyright}>
                    <ListItemIcon>
                        <CopyrightOutlined fontSize="small" />
                    </ListItemIcon>
                    {t("copyright.copyright")}
                </MenuItem>

                <Copyright
                    open={openCopyright}
                    onClose={handleCloseCopyright}
                />

                {/* support */}

                <ConditionalWrapper
                    condition={!isLoggedIn() || !isUsersPortal()}
                    wrapper={children => <Tooltip title={t("msg.please_login")} placement="left" arrow><span>{children}</span></Tooltip>}
                >
                    <MenuItem
                        onClick={handleOpenSupport}
                        disabled={!isLoggedIn() || !isUsersPortal()}
                    >
                        <ListItemIcon>
                            <AlternateEmail fontSize="small" />
                        </ListItemIcon>
                        {t("support.support")}
                    </MenuItem>
                </ConditionalWrapper>

                {/* login/logout */}

                <MenuItem 
                    onClick={isLoggedIn() ? handleLogout : handleLogin} 
                    disabled={disableCredentialControl}
                    divider
                >
                    <ListItemIcon>
                        {isLoggedIn() ? <Logout fontSize="small" /> : <Login fontSize="small" />}
                    </ListItemIcon>
                    {isLoggedIn() ? t(`user.logout`) : t(`user.login`)}
                </MenuItem>

                {/* version */}

                <MenuItem
                    sx={{ pb: 0, justifyContent: "center" }}
                    onClick={() => {
                        handleCloseSettings()
                        onShowWhatsNew?.()
                    }}
                >
                    <Typography sx={{ fontSize: "0.96rem" }}>
                        {t("user.version")}: {process.env.REACT_APP_VERSION}
                    </Typography>
                </MenuItem>

            </Menu>
        </Fragment>
    )
}